import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CreditCard, Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceScopeLabelFromInstance } from '@/lib/service-instance'
import { orgTreeApi } from '@/services/org-tree'
import { getOrgWalletLabel, orgWalletApi, type OrgWallet, type OrgWalletPayload, type OrgWalletVariant } from '@/services/org-wallet'

type WalletFormState = {
  walletVariant: string
  label: string
  accName: string
  accNum: string
  code: string
  branch: string
  contact: string
  apiKey: string
  apiServer: string
  apiUrl: string
  environment: string
}

const defaultFormState: WalletFormState = {
  walletVariant: '',
  label: '',
  accName: '',
  accNum: '',
  code: '',
  branch: '',
  contact: '',
  apiKey: '',
  apiServer: '',
  apiUrl: '',
  environment: '',
}

const toInputValue = (value?: string | number | null) => (value == null ? '' : String(value))

const getVariantId = (wallet: OrgWallet | null) => {
  if (!wallet?.wallet_variant) {
    return ''
  }

  if (typeof wallet.wallet_variant === 'object') {
    return toInputValue(wallet.wallet_variant.id)
  }

  return toInputValue(wallet.wallet_variant)
}

const createFormState = (wallet?: OrgWallet | null): WalletFormState => ({
  walletVariant: getVariantId(wallet ?? null),
  label: toInputValue(wallet?.label),
  accName: toInputValue(wallet?.acc_name),
  accNum: toInputValue(wallet?.acc_num),
  code: toInputValue(wallet?.code),
  branch: toInputValue(wallet?.branch),
  contact: toInputValue(wallet?.contact),
  apiKey: toInputValue(wallet?.api_key),
  apiServer: toInputValue(wallet?.api_server),
  apiUrl: toInputValue(wallet?.api_url),
  environment: toInputValue(wallet?.environment),
})

const getVariantDetails = (wallet: OrgWallet, variants: OrgWalletVariant[]) => {
  if (wallet.wallet_variant && typeof wallet.wallet_variant === 'object') {
    return wallet.wallet_variant
  }

  return variants.find((variant) => String(variant.id) === getVariantId(wallet)) ?? null
}

type WalletGroup = 'cash' | 'gateway' | 'bank' | 'ewallet' | 'upi' | 'thirdparty'

const PAYMENT_GATEWAY = {
  CashFree: 100,
  Stripe: 200,
} as const

const WALLET_GROUP_ORDER: WalletGroup[] = ['gateway', 'bank', 'ewallet', 'upi', 'thirdparty', 'cash']

const WALLET_GROUP_LABELS: Record<WalletGroup, string> = {
  cash: 'Cash',
  gateway: 'Gateway',
  bank: 'Bank',
  ewallet: 'E-Wallet',
  upi: 'UPI',
  thirdparty: 'Third Party',
}

// WalletTypeChoices: Cash=1, Gateway=2, Bank=3, EWallet=4, UPI=5, ThirdParty=6
const getWalletGroup = (variant: OrgWalletVariant | null): WalletGroup | null => {
  if (!variant) return null
  const t = variant.type
  if (t === 1) return 'cash'
  if (t === 2 || variant.pg_type != null) return 'gateway'
  if (t === 3) return 'bank'
  if (t === 4) return 'ewallet'
  if (t === 5) return 'upi'
  if (t === 6) return 'thirdparty'
  return 'gateway'
}

type NodePaymentMethodManagerProps = {
  nodeId: string | number
  orgInstanceId?: string | null
}

export default function NodePaymentMethodManager({ nodeId, orgInstanceId }: NodePaymentMethodManagerProps) {
  const queryClient = useQueryClient()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingWalletId, setEditingWalletId] = useState<string | number | null>(null)
  const [walletForm, setWalletForm] = useState<WalletFormState>(defaultFormState)
  const [walletToDelete, setWalletToDelete] = useState<OrgWallet | null>(null)

  const walletQueryKey = ['org-wallets', nodeId, orgInstanceId]
  const commercePosWalletQueryKey = ['commerce-pos-wallet-methods', nodeId, orgInstanceId]
  const invoicePaymentWalletQueryKey = ['invoice-payment-wallets', orgInstanceId]

  const linkedOrgInstanceQuery = useQuery({
    queryKey: ['node-wallet-linked-org-instance', orgInstanceId],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(orgInstanceId!),
    enabled: Boolean(orgInstanceId),
    staleTime: 60_000,
  })

  const linkedScopeLabel = useMemo(() => {
    if (!orgInstanceId) {
      return null
    }
    if (linkedOrgInstanceQuery.data) {
      return getServiceInstanceScopeLabelFromInstance(linkedOrgInstanceQuery.data)
    }
    return 'Service Instance'
  }, [orgInstanceId, linkedOrgInstanceQuery.data])

  const walletsQuery = useQuery({
    queryKey: walletQueryKey,
    queryFn: () => orgWalletApi.listWallets(orgInstanceId!, { nodeId }),
    enabled: Boolean(nodeId) && Boolean(orgInstanceId),
  })

  const variantsQuery = useQuery({
    queryKey: ['org-wallet-variants'],
    queryFn: orgWalletApi.listWalletVariants,
    staleTime: 60_000,
  })

  const walletDetailQuery = useQuery({
    queryKey: ['org-wallet', editingWalletId],
    queryFn: () => orgWalletApi.getWallet(editingWalletId!),
    enabled: editingWalletId != null,
  })

  useEffect(() => {
    if (editingWalletId == null) {
      setWalletForm(defaultFormState)
      return
    }

    if (walletDetailQuery.data) {
      setWalletForm(createFormState(walletDetailQuery.data))
    }
  }, [editingWalletId, walletDetailQuery.data])

  const selectedVariant = useMemo(
    () => (variantsQuery.data ?? []).find((variant) => String(variant.id) === walletForm.walletVariant) ?? null,
    [variantsQuery.data, walletForm.walletVariant],
  )

  const groupedVariants = useMemo(() => {
    const groups: Record<WalletGroup, OrgWalletVariant[]> = { cash: [], gateway: [], bank: [], ewallet: [], upi: [], thirdparty: [] }
    for (const variant of variantsQuery.data ?? []) {
      const g = getWalletGroup(variant) ?? 'gateway'
      groups[g].push(variant)
    }
    return groups
  }, [variantsQuery.data])

  const invalidateWallets = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: walletQueryKey }),
      queryClient.invalidateQueries({ queryKey: commercePosWalletQueryKey }),
      queryClient.invalidateQueries({ queryKey: invoicePaymentWalletQueryKey }),
    ])
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!orgInstanceId) {
        throw new Error('Missing organization instance')
      }

      if (!walletForm.walletVariant) {
        throw new Error('Wallet type is required')
      }

      const payload: OrgWalletPayload = {
        org_instance: orgInstanceId,
        node: nodeId,
        wallet_variant: walletForm.walletVariant,
        label: walletForm.label,
        acc_name: walletForm.accName,
        acc_num: walletForm.accNum,
        code: walletForm.code,
        branch: walletForm.branch,
        contact: walletForm.contact,
        api_key: walletForm.apiKey,
        api_server: walletForm.apiServer,
        api_url: walletForm.apiUrl,
        environment: walletForm.environment,
      }

      if (editingWalletId != null) {
        return orgWalletApi.updateWallet(editingWalletId, payload)
      }

      return orgWalletApi.createWallet(payload)
    },
    onSuccess: async () => {
      await invalidateWallets()
      setIsDialogOpen(false)
      setEditingWalletId(null)
      setWalletForm(defaultFormState)
      toast.success(editingWalletId != null ? 'Wallet updated successfully' : 'Wallet added successfully')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (walletId: string | number) => orgWalletApi.deleteWallet(walletId),
    onSuccess: async () => {
      await invalidateWallets()
      setWalletToDelete(null)
      toast.success('Wallet deleted successfully')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openCreate = () => {
    setEditingWalletId(null)
    setWalletForm(defaultFormState)
    setIsDialogOpen(true)
  }

  const openEdit = (walletId: string | number) => {
    setEditingWalletId(walletId)
    setIsDialogOpen(true)
  }

  const walletGroup = getWalletGroup(selectedVariant)
  const isCash = walletGroup === 'cash'
  const isBank = walletGroup === 'bank'
  const isUpi = walletGroup === 'upi'
  const isEWallet = walletGroup === 'ewallet'
  const isThirdParty = walletGroup === 'thirdparty'
  const isGateway = walletGroup === 'gateway'
  const isStripeGateway = Number(selectedVariant?.pg_type) === PAYMENT_GATEWAY.Stripe
  const wallets = walletsQuery.data ?? []
  const gatewayCount = wallets.filter((wallet) => getVariantDetails(wallet, variantsQuery.data ?? [])?.pg_type != null).length

  return (
    <>
      <Card className="overflow-hidden rounded-[24px] border-border/80 bg-gradient-to-b from-background to-muted/10 shadow-sm">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-xl">Wallet</CardTitle>
                <CardDescription>Node wallet entries backed by OrgWallet.</CardDescription>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{wallets.length} configured</Badge>
              <Badge variant="outline">{gatewayCount} gateways</Badge>
              <Badge variant={orgInstanceId ? 'outline' : 'destructive'}>{orgInstanceId ? `${linkedScopeLabel} linked` : 'Organization context required'}</Badge>
            </div>
          </div>
          <Button className="shadow-sm" onClick={openCreate} disabled={!orgInstanceId}>
            <Plus className="mr-2 h-4 w-4" />
            Add Wallet
          </Button>
        </CardHeader>
        <CardContent className="space-y-5">
          {!orgInstanceId ? (
            <div className="rounded-3xl border border-dashed border-border/70 bg-linear-to-br from-destructive/5 via-background to-muted/20 p-10 text-center text-sm text-muted-foreground">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
                <ShieldCheck className="h-7 w-7" />
              </div>
              An organization context is required before wallets can be managed for this node.
            </div>
          ) : walletsQuery.isLoading ? (
            <div className="rounded-3xl border border-dashed border-border/70 bg-muted/20 p-10 text-sm text-muted-foreground">
              Loading wallets...
            </div>
          ) : wallets.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border/70 bg-linear-to-br from-primary/5 via-background to-muted/20 p-10 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <CreditCard className="h-7 w-7" />
              </div>
              <p className="font-medium text-foreground">No wallets configured</p>
              <p className="mt-2 text-sm text-muted-foreground">Add a node wallet using OrgWallet to manage manual and gateway-based collection options.</p>
            </div>
          ) : (
            <>
              <div className="rounded-3xl border border-border/70 bg-background/90 p-5 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Wallet Summary</p>
                    <p className="mt-2 text-base font-semibold text-foreground">Review branch, contact, and gateway setup at a glance before editing wallet details.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {wallets.slice(0, 3).map((wallet) => (
                      <Badge key={String(wallet.id)} variant="outline">{getOrgWalletLabel(wallet)}</Badge>
                    ))}
                  </div>
                </div>
              </div>
              <div className="overflow-hidden rounded-3xl border border-border/70 bg-background shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableHead className="pl-5">Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead className="pr-5 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {wallets.map((wallet) => {
                    const variant = getVariantDetails(wallet, variantsQuery.data ?? [])

                    return (
                      <TableRow key={String(wallet.id)}>
                        <TableCell className="pl-5">
                          <div className="flex flex-col gap-1">
                            <span className="font-medium text-foreground">{getOrgWalletLabel(wallet)}</span>
                            {wallet.acc_num ? <span className="text-xs text-muted-foreground">Acct {wallet.acc_num}</span> : null}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline">{variant?.type_name || variant?.name || '-'}</Badge>
                            {(() => { const g = getWalletGroup(variant); return g ? <Badge variant={g === 'gateway' ? 'default' : 'secondary'}>{WALLET_GROUP_LABELS[g]}</Badge> : null })()}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{wallet.contact || '-'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{wallet.branch || '-'}</TableCell>
                        <TableCell className="pr-5">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(wallet.id)}>
                              <Pencil className="mr-1 h-4 w-4" />
                              Edit
                            </Button>
                            <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setWalletToDelete(wallet)}>
                              <Trash2 className="mr-1 h-4 w-4" />
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open)
        if (!open) {
          setEditingWalletId(null)
          setWalletForm(defaultFormState)
        }
      }}>
        <DialogContent className="max-h-[95vh] w-full max-w-3xl p-0 flex flex-col overflow-hidden">
  {/* Header */}
  <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
    <DialogTitle>
      {editingWalletId != null ? 'Edit Wallet' : 'Add Wallet'}
    </DialogTitle>
    <DialogDescription>
      Manage node wallets through OrgWallet.
    </DialogDescription>
  </DialogHeader>

  {/* Scrollable body */}
  <div className="flex-1 overflow-y-auto px-6 pb-4">
    <div className="space-y-5">
              <div className="rounded-2xl border border-border/70 bg-muted/15 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Wallet Type</p>
                {walletGroup ? (
                  <Badge variant={isGateway ? 'default' : 'secondary'}>{WALLET_GROUP_LABELS[walletGroup]}</Badge>
                ) : (
                  <Badge variant="outline">Select type</Badge>
                )}
              </div>
              <div className="mt-4 grid gap-2">
                <Label>Wallet type</Label>
                <Select value={walletForm.walletVariant} onValueChange={(value) => setWalletForm((current) => ({ ...current, walletVariant: value }))}>
                  <SelectTrigger aria-label="Wallet type" className="w-full bg-background/80">
                    <SelectValue placeholder="Choose wallet type" />
                  </SelectTrigger>
                  <SelectContent>
                    {WALLET_GROUP_ORDER.map((group) =>
                      groupedVariants[group].length > 0 ? (
                        <SelectGroup key={group}>
                          <SelectLabel>{WALLET_GROUP_LABELS[group]}</SelectLabel>
                          {groupedVariants[group].map((variant) => (
                            <SelectItem key={String(variant.id)} value={String(variant.id)}>
                              {variant.name || `Variant ${variant.id}`}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ) : null
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-4 grid gap-2">
                <Label htmlFor="wallet_label">Display label</Label>
                <Input
                  id="wallet_label"
                  placeholder="Example: Pay by card"
                  value={walletForm.label}
                  onChange={(event) => setWalletForm((current) => ({ ...current, label: event.target.value }))}
                />
                <p className="text-xs text-muted-foreground">Shown to customers when payment methods are listed.</p>
              </div>
            </div>

            {isCash ? (
              <div className="rounded-2xl border border-dashed border-border/70 bg-muted/15 p-4 text-sm text-muted-foreground">
                Cash is ready to use. No additional details required.
              </div>
            ) : (
              <div className="rounded-2xl border border-border/70 bg-muted/15 p-4">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Account Details</p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="acc_name">{isUpi || isEWallet || isThirdParty ? 'Display name' : 'Account name'}</Label>
                    <Input id="acc_name" value={walletForm.accName} onChange={(event) => setWalletForm((current) => ({ ...current, accName: event.target.value }))} />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="contact">{isUpi ? 'UPI ID / Contact' : isEWallet || isThirdParty ? 'Account ID / Contact' : 'Contact'}</Label>
                    <Input id="contact" value={walletForm.contact} onChange={(event) => setWalletForm((current) => ({ ...current, contact: event.target.value }))} />
                  </div>
                  {isBank ? (
                    <>
                      <div className="grid gap-2">
                        <Label htmlFor="acc_num">Account number</Label>
                        <Input id="acc_num" value={walletForm.accNum} onChange={(event) => setWalletForm((current) => ({ ...current, accNum: event.target.value }))} />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="code">IFSC / Sort code</Label>
                        <Input id="code" value={walletForm.code} onChange={(event) => setWalletForm((current) => ({ ...current, code: event.target.value }))} />
                      </div>
                      <div className="grid gap-2 sm:col-span-2">
                        <Label htmlFor="branch">Branch</Label>
                        <Input id="branch" value={walletForm.branch} onChange={(event) => setWalletForm((current) => ({ ...current, branch: event.target.value }))} />
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            )}

            {isGateway ? (
              <div className="rounded-2xl border border-border/70 bg-muted/15 p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Integration</p>
                  <Badge variant="outline">{isStripeGateway ? 'Stripe' : 'OrgWallet'}</Badge>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="api_key">{isStripeGateway ? 'Stripe secret key' : 'API key'}</Label>
                    <Input id="api_key" value={walletForm.apiKey} onChange={(event) => setWalletForm((current) => ({ ...current, apiKey: event.target.value }))} />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="api_server">{isStripeGateway ? 'Stripe publishable key' : 'API server'}</Label>
                    <Input id="api_server" value={walletForm.apiServer} onChange={(event) => setWalletForm((current) => ({ ...current, apiServer: event.target.value }))} />
                  </div>
                  {isStripeGateway ? null : (
                    <>
                      <div className="grid gap-2">
                        <Label htmlFor="environment">Environment</Label>
                        <Input id="environment" value={walletForm.environment} onChange={(event) => setWalletForm((current) => ({ ...current, environment: event.target.value }))} />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="api_url">API URL</Label>
                        <Input id="api_url" value={walletForm.apiUrl} onChange={(event) => setWalletForm((current) => ({ ...current, apiUrl: event.target.value }))} />
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : null}
    </div>
  </div>

  {/* Footer */}
  <DialogFooter className="border-t bg-background px-6 py-4 shrink-0">
    <Button
      variant="outline"
      onClick={() => setIsDialogOpen(false)}
      disabled={saveMutation.isPending}
    >
      Cancel
    </Button>

    <Button
      onClick={() => saveMutation.mutate()}
      disabled={
        saveMutation.isPending ||
        walletDetailQuery.isLoading
      }
    >
      {saveMutation.isPending
        ? 'Saving...'
        : editingWalletId != null
        ? 'Update Wallet'
        : 'Save Wallet'}
    </Button>
  </DialogFooter>
</DialogContent>
   
      </Dialog>

      <Dialog open={walletToDelete != null} onOpenChange={(open) => {
        if (!open) {
          setWalletToDelete(null)
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Wallet</DialogTitle>
            <DialogDescription>
              Remove {walletToDelete?.acc_name || 'this wallet'} from the node.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-2xl border border-border/70 bg-muted/15 p-4 text-sm text-muted-foreground">
            This will remove the selected OrgWallet entry from the current node configuration.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWalletToDelete(null)} disabled={deleteMutation.isPending}>Cancel</Button>
            <Button variant="destructive" onClick={() => walletToDelete && deleteMutation.mutate(walletToDelete.id)} disabled={deleteMutation.isPending || !walletToDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}